<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  // Everything the preview endpoint needs to build a throwaway campaign.
  previewContext: {
    type: Object,
    default: () => ({}),
  },
  // Approved-template campaigns cannot use the agent: the copy is fixed by Meta.
  unavailable: {
    type: Boolean,
    default: false,
  },
  unavailableReason: {
    type: String,
    default: '',
  },
  // Validation message for the prompt. The prompt is required once the agent is
  // switched on, and without surfacing this the submit button just sat disabled
  // with nothing on screen explaining why.
  promptError: {
    type: String,
    default: '',
  },
});

const enabled = defineModel('enabled', { type: Boolean, default: false });
const prompt = defineModel('prompt', { type: String, default: '' });

const { t } = useI18n();
const store = useStore();

const samples = ref([]);
const previewError = ref('');
const isPreviewing = ref(false);

const canPreview = computed(
  () => enabled.value && prompt.value.trim().length > 0 && !isPreviewing.value
);

const handlePreview = async () => {
  isPreviewing.value = true;
  previewError.value = '';
  samples.value = [];
  try {
    const data = await store.dispatch('campaigns/previewAiMessages', {
      ...props.previewContext,
      ai_prompt: prompt.value,
    });
    samples.value = data?.payload ?? [];
  } catch (error) {
    previewError.value =
      error?.response?.data?.error || t('CAMPAIGN.AI_AGENT.PREVIEW.ERROR');
  } finally {
    isPreviewing.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-0.5 min-w-0">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.AI_AGENT.TITLE') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.AI_AGENT.SUBTITLE') }}
        </span>
      </div>
      <Switch v-if="!unavailable" v-model="enabled" class="mt-1" />
    </div>

    <p v-if="unavailable" class="text-xs text-n-amber-11">
      {{ unavailableReason || t('CAMPAIGN.AI_AGENT.UNAVAILABLE') }}
    </p>

    <template v-else-if="enabled">
      <TextArea
        v-model="prompt"
        :label="t('CAMPAIGN.AI_AGENT.PROMPT.LABEL')"
        :placeholder="t('CAMPAIGN.AI_AGENT.PROMPT.PLACEHOLDER')"
        :rows="5"
        :message="promptError"
        :message-type="promptError ? 'error' : 'info'"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.AI_AGENT.PROMPT.INFO') }}
      </p>

      <div class="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-sparkles"
          :label="t('CAMPAIGN.AI_AGENT.PREVIEW.BUTTON')"
          :disabled="!canPreview"
          @click="handlePreview"
        />
        <span
          v-if="isPreviewing"
          class="flex items-center gap-1.5 text-xs text-n-slate-11"
        >
          <Spinner />
          {{ t('CAMPAIGN.AI_AGENT.PREVIEW.LOADING') }}
        </span>
      </div>

      <p v-if="previewError" class="text-xs text-n-ruby-11">
        {{ previewError }}
      </p>

      <ul v-if="samples.length" class="flex flex-col gap-2 m-0 list-none">
        <li
          v-for="(sample, index) in samples"
          :key="sample.contact_id ?? index"
          class="flex flex-col gap-1 p-3 rounded-lg bg-n-solid-2"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ sample.name || t('CAMPAIGN.AI_AGENT.PREVIEW.UNNAMED') }}
          </span>
          <span
            v-if="sample.error"
            class="text-xs whitespace-pre-line text-n-ruby-11"
          >
            {{ sample.error }}
          </span>
          <span v-else class="text-sm whitespace-pre-line text-n-slate-12">
            {{ sample.message }}
          </span>
        </li>
      </ul>
    </template>
  </div>
</template>
